import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ArticleService } from '../../services/article.service';
import { AuthService } from '../../services/auth.service';
import { Article, TokenSentence, ArticleTextLevels } from '../../entities/article.model';
import { User } from '../../entities/user.model';
import { IonicModule, ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AppTranslateService } from 'src/app/services/translate.service';
import { WordDetailsModalComponent } from 'src/app/components/word-details-modal/word-details-modal.component';
import { EXAMPLE_ARTICLES } from './example_articles';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from 'src/app/services/gtag.service';
import { DisplayService } from 'src/app/services/display.service';
import { TextToSpeechService } from 'src/app/services/text-to-speech.service';
import { doc, updateDoc } from '@angular/fire/firestore';
import { Firestore } from '@angular/fire/firestore';

interface WordToken {
  text: string;
  isKeyWord: boolean;
}

@Component({
  selector: 'app-article',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, FormsModule, TranslateModule],
  templateUrl: './article.page.html',
  styleUrls: ['./article.page.scss'],
})
export class ArticlePage implements OnInit, AfterViewInit {
  article: Article | undefined;
  articleId: string = "";
  user: User | null = null;
  selectedLevel: number = 1; // Default level
  targetLang: string = ''; // Default language
  nativeLang: string = 'en';
  translateMode: boolean = false;
  loadingTranslation: boolean = false;
  article_parsed: WordToken[][] = [];
  title_parsed: WordToken[] = [];
  isTouring: boolean = false;
  bookmarked: boolean = false;
  loadingAudio: boolean = false;



  constructor(
    private route: ActivatedRoute,
    private articleService: ArticleService,
    private authService: AuthService,
    private translateService: AppTranslateService,
    private router: Router,
    private modalController: ModalController,
    private userService: UserService,
    private gTagService: AnalyticsService, // Assuming you have a GTagService for Google Analytics
    private displayService: DisplayService,
    private firestore: Firestore,
    private textToSpeechService: TextToSpeechService
  ) {}

  ngOnInit() {
    document.title = 'Article';
    
    this.authService.currentUser$.subscribe(user => {
      const previousUser = this.user;
      this.user = user;
      if (this.user) {
        console.log(this.user.uid);
        this.nativeLang = this.user.nativeLanguage;
        this.targetLang = this.user.targetLanguage;
        
        // Only reset selectedLevel if it's the initial load or the user's actual level changed
        if (!previousUser || 
            previousUser.uid !== this.user.uid || 
            previousUser.level[this.targetLang] !== this.user.level[this.targetLang]) {
          this.selectedLevel = this.user.level[this.targetLang];
        }
      }
      
      // Only initialize article on first load or user change
      if (!previousUser || !user || previousUser.uid !== user.uid) {
        this.initializeArticle();
        this.wakeUpEndpoints();
      }
    });
  }

  ngAfterViewInit() {
    // No longer needed - styling is handled at app level
  }

  ionViewDidEnter() {
    // No longer needed - styling is handled at app level
  }

  wakeUpEndpoints() {
    // Send dummy requests to warm up both endpoints
    if (environment.production) {
      this.translateService.wakeUpEndpoints();
      this.textToSpeechService.wakeUpEndpoint();
    }
  }

  initializeArticle(){
    const articleId = this.route.snapshot.paramMap.get('id');
    if (!articleId) {
      console.error('No article ID provided in route');
      return;
    }

    // Check if articleId is a language code and use example article if so
    if (EXAMPLE_ARTICLES[articleId]) {
      this.isTouring = true;
      this.articleId = articleId;
      this.article = EXAMPLE_ARTICLES[articleId];
      this.targetLang = articleId; // Set target language to the example article's language
      this.parseTokenizedArticle();
      this.parseTokenizedTitle();
      return;
    }
    // Get article by id
    this.articleId = articleId;
    // Fetch the article by ID
    this.articleService.getArticleById(articleId).subscribe(article => {
      this.article = article;
      if (this.article) {
        // Set the target language based on the article's language
        this.targetLang = this.article.language;
        if (!this.user) {
          // If user is not logged in, set the native language to the article's language
          this.nativeLang = this.translateService.nativeLanguage;
          let paramsLevel = this.getLevelFromParams();
          if (paramsLevel !== -1) {
            this.selectedLevel = paramsLevel;
          } else {
            this.selectedLevel = 1; // Default to 1 if user is not logged in
          }
          const levelKey = this.selectedLevel.toString() as '1' | '2' | '3';
          document.title = `${this.article.title[this.targetLang][levelKey]}`;
        }
        else {
          // Set bookmarked
          this.bookmarked = this.user.bookmarks.includes(this.articleId);
        }
        this.parseTokenizedArticle();
        this.parseTokenizedTitle();
      } else {
        console.error('Article not found');
        // Redirect to /news
        this.router.navigate(['/news']);
      }
    }
    );
  }

  parseTokenizedArticle() {
    if (!this.article) return;
    const lang = this.targetLang;
    const level = this.selectedLevel.toString();
    const tokenizedLevel = this.article.tokenizedArticle?.[lang]?.[level as keyof typeof this.article.tokenizedArticle[typeof lang]];

    if (tokenizedLevel && Array.isArray(tokenizedLevel)) {
      // Check if it's the new backend format (TokenSentence[]) or legacy format (string[][])
      const firstElement = tokenizedLevel[0];
      if (firstElement && typeof firstElement === 'object' && 'text' in firstElement) {
        // New backend format: TokenSentence[] with keywords array
        this.article_parsed = tokenizedLevel.map((tokenSentence: TokenSentence) => {
          const keywords = new Set(tokenSentence.keywords || []);
          return tokenSentence.text.map(word => {
            // Match words like <friend>, <сотрудничестве>, <test>., <friend!>, etc.
            // Handle punctuation that comes after the closing bracket
            const angleBracketMatch = word.match(/^"?<([^<>]+)>"?(.*)$/);
            if (angleBracketMatch) {
              const keywordText = angleBracketMatch[1];
              const punctuation = angleBracketMatch[2];
              return {
                text: keywordText + punctuation, // Include any trailing punctuation
                isKeyWord: true
              };
            }
            return {
              text: word,
              isKeyWord: false
            };
          });
        });
      } else {
        // Legacy format: string[][] (array of sentence arrays)
        this.article_parsed = (tokenizedLevel as unknown as string[][]).map((sentence: string[]) => {
          return sentence.map((word: string) => this.processWordToken(word));
        });
      }
    } else if (tokenizedLevel && typeof tokenizedLevel === 'object') {
      // Legacy format: {0: [...], 1: [...], ...} with angle brackets
      const sentenceIndices = Object.keys(tokenizedLevel).sort((a, b) => Number(a) - Number(b));
      this.article_parsed = sentenceIndices.map(idx => {
        const sentence = (tokenizedLevel as any)[Number(idx)];
        return sentence.map((word: string) => this.processWordToken(word));
      });
    } else {
      this.article_parsed = [];
    }
  }

  private processWordToken(word: string): WordToken {
    // Check if the word is in angle brackets (legacy format)
    // Handle punctuation that comes after the closing bracket
    const angleBracketMatch = word.match(/^<([^<>]+)>(.*)$/);
    if (angleBracketMatch) {
      const keywordText = angleBracketMatch[1];
      const punctuation = angleBracketMatch[2];
      return {
        text: keywordText + punctuation, // Include any trailing punctuation
        isKeyWord: true
      };
    }
    return {
      text: word,
      isKeyWord: false
    };
  }

  parseTokenizedTitle() {
    if (!this.article) return;
    const lang = this.targetLang;
    const level = this.selectedLevel.toString();
    const tokenizedTitleLevel = this.article.tokenizedTitle?.[lang]?.[level];
    
    if (tokenizedTitleLevel && Array.isArray(tokenizedTitleLevel) && tokenizedTitleLevel.length > 0) {
      // Check if it's the new backend format (TokenSentence[]) or legacy format (string[][])
      const firstElement = tokenizedTitleLevel[0];
      if (firstElement && typeof firstElement === 'object' && 'text' in firstElement) {
        // New backend format: TokenSentence[]
        const titleSentence = firstElement as TokenSentence;
        // Make all title words clickable but not highlighted (ignore keywords)
        this.title_parsed = titleSentence.text.map(word => ({
          text: word,
          isKeyWord: false // Always false for title words
        }));
      } else {
        // Legacy format: string[][] (array containing one sentence array)
        const titleTokens = firstElement as string[];
        this.title_parsed = titleTokens.map((word: string) => ({
          text: word.startsWith('<') && word.endsWith('>') ? word.slice(1, -1) : word,
          isKeyWord: false // Ignore keywords in title, just make clickable
        }));
      }
    } else if (tokenizedTitleLevel && typeof tokenizedTitleLevel === 'object' && !Array.isArray(tokenizedTitleLevel)) {
      // Legacy format: {0: [...], 1: [...], ...} with potential angle brackets
      const titleTokens = (tokenizedTitleLevel as any)[0] || [];
      this.title_parsed = titleTokens.map((word: string) => ({
        text: word.startsWith('<') && word.endsWith('>') ? word.slice(1, -1) : word,
        isKeyWord: false // Ignore keywords in title, just make clickable
      }));
    } else {
      this.title_parsed = [];
    }
  }

  changeLevel(event: any) {
    const level = event.detail.value;
    this.selectedLevel = level;

    // Update the URL with the new level
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { level: this.selectedLevel },
      queryParamsHandling: 'merge', // Merge with existing query parameters
    });
    this.parseTokenizedArticle();
    this.parseTokenizedTitle();
  }

  goToArticle() {
    if (this.article) {
      window.open(this.article.originalUrl, '_blank');
    }
  }

  translate() {
    if(this.article?.articleText[this.nativeLang]){
      this.translateMode = !this.translateMode;
      if (!this.translateMode) {
        this.parseTokenizedArticle(); // Re-parse to show original text
        this.parseTokenizedTitle(); // Re-parse to show original title
      }
    }
    else {
      this.loadingTranslation = true;
      this.translateService.translateArticle(this.articleId, this.nativeLang).subscribe(
        (response) => {
          this.article = response.article as Article;
          this.translateMode = !this.translateMode;
          this.loadingTranslation = false;
        }
      );
    }
  }

  getLevelFromParams() {
    const levelParam = this.route.snapshot.queryParamMap.get('level');
    if (levelParam) {
      const level = parseInt(levelParam, 10);
      if ([1, 2, 3].includes(level)) {
        return level;
      }
    }
    return -1; // Default level
  }

  makeContextSentence(sentence_idx: number): string {
    if (sentence_idx < 0 || sentence_idx >= this.article_parsed.length) {
      console.error('Invalid sentence index:', sentence_idx);
      return '';
    }
    const isJapaneseOrChinese = this.targetLang === 'ja' || this.targetLang === 'zh';
    // Join without spaces for Japanese, with spaces for others
    return isJapaneseOrChinese
      ? this.article_parsed[sentence_idx].map(token => token.text).join('')
      : this.article_parsed[sentence_idx].map(token => token.text).join(' ');
  }
  
  async openWordDetails(word: string, sentence_idx: number = 0, title: boolean = false) {
    this.gTagService.trackEvent('word_details', 'open', word);
    // Join without spaces for Japanese, with spaces for others
    var context_sentence: string;
    if (title) {
      // If title, use the parsed title instead
      const levelKey = this.selectedLevel.toString() as '1' | '2' | '3';
      context_sentence = this.article?.title[this.targetLang]?.[levelKey] || '';
    }
    else {
      context_sentence = this.makeContextSentence(sentence_idx);
    }
    if (!context_sentence) {
      console.error('Context sentence not found for the given index:', sentence_idx);
      return;
    }
    // Remove any punctuation from the word
    word = word.replace(/[.,!?;:()「」『』【】［］｛｝〈〉《》［］・～…—ー、。।]/g, '').trim();
    // Check if the word is empty after removing punctuation
    if (!word) {
      console.error('Word is empty after removing punctuation');
      return;
    }

    // Open the modal as a sheet modal (bottom sheet)
    const modal = await this.modalController.create({
      component: WordDetailsModalComponent,
      componentProps: {
        word: word,
        contextSentence: context_sentence,
        targetLanguage: this.targetLang,
        nativeLanguage: this.nativeLang,
        proficiencyLevel: this.user ? this.user.level[this.targetLang] : 1,
        articleId: this.articleId
      },
      breakpoints: [0, 0.5, 0.9],
      initialBreakpoint: 0.5,
      handle: true,
      cssClass: 'word-sheet-modal'
    });
    await modal.present();
  }

  getDirection(): 'rtl' | 'ltr' {
    // Determinte the current language being used, targetLang if !this.translateMode, otherwise nativeLang
    const currentLang = this.translateMode ? this.nativeLang : this.targetLang;
    // Check if the current language is Arabic or Hebrew
    if (currentLang === 'ar' || currentLang === 'he') {
      return 'rtl'; // Right-to-left
    }
    return 'ltr'; // Left-to-right
  }

  toggleBookMark(): Promise<void> {
    this.bookmarked = !this.bookmarked; // Toggle the bookmarked state
    return this.userService.toggleBookMark(this.articleId,this.bookmarked);
  }

  // Helper methods for safe type casting
  getArticleTitle(lang: string, level: number): string {
    const levelKey = level.toString() as '1' | '2' | '3';
    return this.article?.title[lang]?.[levelKey] || '';
  }

  getArticleText(lang: string, level: number): string {
    const levelKey = level.toString() as '1' | '2' | '3';
    return this.article?.articleText[lang]?.[levelKey] || '';
  }

  /**
   * Increase text size
   */
  increaseTextSize(): void {
    this.displayService.increaseTextSize();
    this.saveDisplayPreferences();
  }

  /**
   * Decrease text size
   */
  decreaseTextSize(): void {
    this.displayService.decreaseTextSize();
    this.saveDisplayPreferences();
  }

  /**
   * Toggle dark mode
   */
  toggleDarkMode(): void {
    this.displayService.toggleDarkMode();
    this.saveDisplayPreferences();
  }

  /**
   * Save display preferences without triggering navigation or level reset
   */
  private async saveDisplayPreferences(): Promise<void> {
    if (this.user) {
      // Update the local user object to keep it in sync
      this.user.textSize = this.displayService.getCurrentTextSize();
      this.user.darkMode = this.displayService.getCurrentDarkMode();
      
      try {
        // Save to Firestore directly without triggering authService.setUser()
        const userDocRef = doc(this.firestore, `users/${this.user.uid}`);
        await updateDoc(userDocRef, {
          textSize: this.user.textSize,
          darkMode: this.user.darkMode
        });
        
        console.log('Display preferences saved successfully');
      } catch (error) {
        console.error('Error saving display preferences:', error);
      }
    }
  }

  /**
   * Get current text size for display
   */
  getCurrentTextSize(): number {
    return this.displayService.getCurrentTextSize();
  }

  /**
   * Get current dark mode state
   */
  isDarkMode(): boolean {
    return this.displayService.getCurrentDarkMode();
  }

  /**
   * Vocalize the current article using text-to-speech
   */
  async vocalize(): Promise<void> {
    if (!this.article) {
      console.error('No article to vocalize');
      return;
    }

    this.loadingAudio = true;
    
    try {
      const levelKey = this.selectedLevel.toString() as '1' | '2' | '3';
      await this.textToSpeechService.vocalizeArticle(this.article, levelKey);
      this.gTagService.trackEvent('article_vocalize', 'play', this.articleId);
    } catch (error) {
      console.error('Error vocalizing article:', error);
    } finally {
      this.loadingAudio = false;
    }
  }

  /**
   * Stop audio playback
   */
  stopAudio(): void {
    this.textToSpeechService.stopCurrentAudio();
  }

  /**
   * Check if audio is currently playing
   */
  isAudioPlaying(): boolean {
    return this.textToSpeechService.isCurrentlyPlaying();
  }
}
